/**
 * COVERFECT — P1 Size Helper Widget (v6)
 * ===================================
 * Modal/popup with Size Finder + Size Chart tabs.
 * Replaces old static size chart popup entirely.
 *
 * v6 changes (from v5):
 * - Fix: trigger skips Quick View fieldsets (findMainFieldset)
 * - Fix: "Select This Size" switches P0 depth toggle for deep variants
 * - Fix: compare_at_price (strikethrough) updates correctly on variant change
 * - All querySelector calls scoped to main product section
 *
 * Deploy: Shopify Admin → Customize → Add Custom Liquid section → paste:
 *   <div id="cf-size-helper"></div>
 *   <script> ...this file... </script>
 */

(function () {
  'use strict';

  // ── CONFIG ──────────────────────────────────────────────────────────
  const CONTAINER_ID = 'cf-size-helper';

  // ── SIZE CHART DATA ──────────────────────────────────────────────────
  // Part icons (front-view sofa, highlighted part)
  const PART_ICONS = {
    armrest: `<svg viewBox="0 0 40 32" fill="none"><rect x="8" y="14" width="24" height="12" rx="2" stroke="#ccc" stroke-width="1.2" fill="none"/><rect x="8" y="6" width="24" height="10" rx="2" stroke="#ccc" stroke-width="1.2" fill="none"/><rect x="2" y="8" width="7" height="18" rx="2" fill="currentColor" opacity="0.85"/><rect x="31" y="8" width="7" height="18" rx="2" fill="currentColor" opacity="0.85"/></svg>`,
    backrest: `<svg viewBox="0 0 40 32" fill="none"><rect x="8" y="14" width="24" height="12" rx="2" stroke="#ccc" stroke-width="1.2" fill="none"/><rect x="8" y="5" width="24" height="10" rx="2" fill="currentColor" opacity="0.85"/><rect x="2" y="8" width="7" height="18" rx="2" stroke="#ccc" stroke-width="1.2" fill="none"/><rect x="31" y="8" width="7" height="18" rx="2" stroke="#ccc" stroke-width="1.2" fill="none"/></svg>`,
    seat: `<svg viewBox="0 0 40 32" fill="none"><rect x="8" y="14" width="24" height="12" rx="2" fill="currentColor" opacity="0.85"/><rect x="8" y="5" width="24" height="10" rx="2" stroke="#ccc" stroke-width="1.2" fill="none"/><rect x="2" y="8" width="7" height="18" rx="2" stroke="#ccc" stroke-width="1.2" fill="none"/><rect x="31" y="8" width="7" height="18" rx="2" stroke="#ccc" stroke-width="1.2" fill="none"/></svg>`,
  };

  // Sub-tab 1: Armrest & Back
  const SC_ARM_BACK = [
    { title: 'Armrest Cover', icon: PART_ICONS.armrest, rows: [
      { measure: '< 28" / < 70cm', size: '28"x28"', cm: '70x70cm' },
      { measure: '28"-35" / 70-90cm', size: '35"x35"', cm: '90x90cm' },
    ]},
    { title: 'Back Cover', icon: PART_ICONS.backrest, rows: [
      { measure: '< 35" / < 90cm', size: '35"x35"', cm: '90x90cm' },
      { measure: '36"-43" / 91-110cm', size: '43"x43"', cm: '110x110cm' },
    ]},
  ];
  // Sub-tab 2: Seat / Chaise
  const SC_SEAT = [
    { title: 'Standard Depth (28"-35" / 70-90cm)', rows: [
      { length: '28"-35"', lcm: '70-90cm', size: '35"x35"', scm: '90x90cm' },
      { length: '40"-47"', lcm: '100-121cm', size: '35"x47"', scm: '90x121cm' },
      { length: '48"-63"', lcm: '122-160cm', size: '35"x63"', scm: '90x160cm' },
      { length: '64"-71"', lcm: '161-180cm', size: '35"x71"', scm: '90x180cm' },
      { length: '72"-83"', lcm: '181-210cm', size: '35"x83"', scm: '90x210cm' },
      { length: '84"-95"', lcm: '211-240cm', size: '35"x95"', scm: '90x240cm' },
    ]},
    { title: 'Deep / Plus (36"-43" / 91-110cm)', rows: [
      { length: '36"-43"', lcm: '91-110cm', size: '43"x43"', scm: '110x110cm' },
      { length: '44"-63"', lcm: '111-160cm', size: '43"x63"', scm: '110x160cm' },
      { length: '64"-71"', lcm: '163-180cm', size: '43"x71"', scm: '110x180cm' },
      { length: '72"-83"', lcm: '181-210cm', size: '43"x83"', scm: '110x210cm' },
      { length: '84"-95"', lcm: '211-240cm', size: '43"x95"', scm: '110x240cm' },
    ]},
  ];
  // Sub-tab 3: Full Sets (with SVG top-down sofa icons)
  const SET_ICONS = {
    loveseat: `<svg viewBox="0 0 56 32" fill="none"><rect x="6" y="2" width="44" height="8" rx="2" fill="currentColor" opacity=".12" stroke="currentColor" stroke-width="1.2"/><rect x="6" y="10" width="21" height="16" rx="2" fill="currentColor" opacity=".18" stroke="currentColor" stroke-width="1.2"/><rect x="29" y="10" width="21" height="16" rx="2" fill="currentColor" opacity=".18" stroke="currentColor" stroke-width="1.2"/><rect x="1" y="2" width="6" height="28" rx="2" fill="currentColor" opacity=".25" stroke="currentColor" stroke-width="1.2"/><rect x="49" y="2" width="6" height="28" rx="2" fill="currentColor" opacity=".25" stroke="currentColor" stroke-width="1.2"/></svg>`,
    standard: `<svg viewBox="0 0 68 32" fill="none"><rect x="6" y="2" width="56" height="8" rx="2" fill="currentColor" opacity=".12" stroke="currentColor" stroke-width="1.2"/><rect x="6" y="10" width="18" height="16" rx="2" fill="currentColor" opacity=".18" stroke="currentColor" stroke-width="1.2"/><rect x="25" y="10" width="18" height="16" rx="2" fill="currentColor" opacity=".18" stroke="currentColor" stroke-width="1.2"/><rect x="44" y="10" width="18" height="16" rx="2" fill="currentColor" opacity=".18" stroke="currentColor" stroke-width="1.2"/><rect x="1" y="2" width="6" height="28" rx="2" fill="currentColor" opacity=".25" stroke="currentColor" stroke-width="1.2"/><rect x="61" y="2" width="6" height="28" rx="2" fill="currentColor" opacity=".25" stroke="currentColor" stroke-width="1.2"/></svg>`,
    four: `<svg viewBox="0 0 80 32" fill="none"><rect x="6" y="2" width="68" height="8" rx="2" fill="currentColor" opacity=".12" stroke="currentColor" stroke-width="1.2"/><rect x="6" y="10" width="16" height="16" rx="2" fill="currentColor" opacity=".18" stroke="currentColor" stroke-width="1.2"/><rect x="23" y="10" width="16" height="16" rx="2" fill="currentColor" opacity=".18" stroke="currentColor" stroke-width="1.2"/><rect x="40" y="10" width="16" height="16" rx="2" fill="currentColor" opacity=".18" stroke="currentColor" stroke-width="1.2"/><rect x="57" y="10" width="17" height="16" rx="2" fill="currentColor" opacity=".18" stroke="currentColor" stroke-width="1.2"/><rect x="1" y="2" width="6" height="28" rx="2" fill="currentColor" opacity=".25" stroke="currentColor" stroke-width="1.2"/><rect x="73" y="2" width="6" height="28" rx="2" fill="currentColor" opacity=".25" stroke="currentColor" stroke-width="1.2"/></svg>`,
    // 3-seat L: left arm | 2-cushion main + chaise | right arm (same height as left)
    lshape3: `<svg viewBox="0 0 70 40" fill="none"><rect x="6" y="2" width="40" height="7" rx="2" fill="currentColor" opacity=".12" stroke="currentColor" stroke-width="1.2"/><rect x="6" y="9" width="19" height="14" rx="2" fill="currentColor" opacity=".18" stroke="currentColor" stroke-width="1.2"/><rect x="27" y="9" width="19" height="14" rx="2" fill="currentColor" opacity=".18" stroke="currentColor" stroke-width="1.2"/><rect x="1" y="2" width="6" height="24" rx="2" fill="currentColor" opacity=".25" stroke="currentColor" stroke-width="1.2"/><rect x="46" y="2" width="16" height="7" rx="2" fill="currentColor" opacity=".12" stroke="currentColor" stroke-width="1.2"/><rect x="46" y="9" width="16" height="29" rx="2" fill="currentColor" opacity=".18" stroke="currentColor" stroke-width="1.2"/><rect x="63" y="2" width="6" height="24" rx="2" fill="currentColor" opacity=".25" stroke="currentColor" stroke-width="1.2"/></svg>`,
    // 4-seat L: left arm | 3-cushion main + chaise | right arm (same height as left)
    lshape4: `<svg viewBox="0 0 82 40" fill="none"><rect x="6" y="2" width="52" height="7" rx="2" fill="currentColor" opacity=".12" stroke="currentColor" stroke-width="1.2"/><rect x="6" y="9" width="16" height="14" rx="2" fill="currentColor" opacity=".18" stroke="currentColor" stroke-width="1.2"/><rect x="24" y="9" width="16" height="14" rx="2" fill="currentColor" opacity=".18" stroke="currentColor" stroke-width="1.2"/><rect x="42" y="9" width="16" height="14" rx="2" fill="currentColor" opacity=".18" stroke="currentColor" stroke-width="1.2"/><rect x="1" y="2" width="6" height="24" rx="2" fill="currentColor" opacity=".25" stroke="currentColor" stroke-width="1.2"/><rect x="58" y="2" width="16" height="7" rx="2" fill="currentColor" opacity=".12" stroke="currentColor" stroke-width="1.2"/><rect x="58" y="9" width="16" height="29" rx="2" fill="currentColor" opacity=".18" stroke="currentColor" stroke-width="1.2"/><rect x="75" y="2" width="6" height="24" rx="2" fill="currentColor" opacity=".25" stroke="currentColor" stroke-width="1.2"/></svg>`,
  };
  const SC_SETS = [
    { name: 'Loveseat Set', pcs: '5pcs', icon: SET_ICONS.loveseat, items: [
      { part: 'Seat', size: '35"x63"', qty: '1pc' },
      { part: 'Backrest', size: '35"x35"', qty: '2pcs' },
      { part: 'Armrest', size: '28"x28"', qty: '2pcs' },
    ]},
    { name: '3-Seat + 2 Arms', pcs: '3pcs', icon: SET_ICONS.standard, items: [
      { part: 'Seat', size: '35"x83"', qty: '1pc' },
      { part: 'Armrest', size: '28"x28"', qty: '2pcs' },
    ]},
    { name: '3-Seat Full Set', pcs: '6pcs', icon: SET_ICONS.standard, popular: true, items: [
      { part: 'Seat', size: '35"x83"', qty: '1pc' },
      { part: 'Backrest', size: '35"x35"', qty: '3pcs' },
      { part: 'Armrest', size: '28"x28"', qty: '2pcs' },
    ]},
    { name: '4-Seat Full Set', pcs: '7pcs', icon: SET_ICONS.four, items: [
      { part: 'Seat', size: '35"x95"', qty: '1pc' },
      { part: 'Backrest', size: '35"x35"', qty: '4pcs' },
      { part: 'Armrest', size: '28"x28"', qty: '2pcs' },
    ]},
    { name: '3-Seat L-Shape', pcs: '7pcs', icon: SET_ICONS.lshape3, items: [
      { part: 'Seat', size: '35"x63"', qty: '1pc' },
      { part: 'Chaise', size: '35"x63"', qty: '1pc' },
      { part: 'Backrest', size: '35"x35"', qty: '3pcs' },
      { part: 'Armrest', size: '28"x28"', qty: '2pcs' },
    ]},
    { name: '4-Seat L-Shape', pcs: '8pcs', icon: SET_ICONS.lshape4, items: [
      { part: 'Seat', size: '35"x83"', qty: '1pc' },
      { part: 'Chaise', size: '35"x63"', qty: '1pc' },
      { part: 'Backrest', size: '35"x35"', qty: '4pcs' },
      { part: 'Armrest', size: '28"x28"', qty: '2pcs' },
    ]},
  ];

  // Size Finder: measurement ranges -> recommended variant
  const SIZE_RULES = {
    armrest: [
      { maxWidth: 28, variant: '28"x28"', label: 'Standard Arm 28"x28"' },
      { maxWidth: 35, variant: '35"x35"', label: 'Wide Arm 35"x35"' },
      { maxWidth: 99, variant: '35"x35"', label: 'Wide Arm 35"x35"', note: 'Your arm is wider than 35" — Wide Arm is our largest and may not fully cover. Send us a photo of your sofa and we\'ll help find the best option.' },
    ],
    backrest: [
      { maxWidth: 35, maxLength: 35,  variant: '35"x35"',  label: 'Single Back 35"x35"' },
      { maxWidth: 35, maxLength: 47,  variant: '35"x47"',  label: 'Back Strip 35"x47"' },
      { maxWidth: 35, maxLength: 63,  variant: '35"x63"',  label: 'Back Strip 35"x63"' },
      { maxWidth: 35, maxLength: 71,  variant: '35"x71"',  label: 'Back Strip 35"x71"' },
      { maxWidth: 35, maxLength: 83,  variant: '35"x83"',  label: 'Back Strip 35"x83"' },
      { maxWidth: 35, maxLength: 95,  variant: '35"x95"',  label: 'Back Strip 35"x95"' },
      { maxWidth: 35, maxLength: 999, variant: '35"x95"',  label: 'Back Strip 35"x95"', note: 'Your back is longer than 95" — our largest single piece. You may need 2 pieces side by side, or send us a photo for help.' },
      { maxWidth: 43, maxLength: 43,  variant: '43"x43"',  label: 'Deep Back 43"x43"' },
      { maxWidth: 43, maxLength: 63,  variant: '43"x63"',  label: 'Deep Back Strip 43"x63"' },
      { maxWidth: 43, maxLength: 71,  variant: '43"x71"',  label: 'Deep Back Strip 43"x71"' },
      { maxWidth: 43, maxLength: 83,  variant: '43"x83"',  label: 'Deep Back Strip 43"x83"' },
      { maxWidth: 43, maxLength: 95,  variant: '43"x95"',  label: 'Deep Back Strip 43"x95"' },
      { maxWidth: 43, maxLength: 999, variant: '43"x95"',  label: 'Deep Back Strip 43"x95"', note: 'Your back is longer than 95" — our largest single piece. You may need 2 pieces side by side, or send us a photo for help.' },
    ],
    seat: [
      { maxWidth: 35, maxLength: 35,  variant: '35"x35"',  label: 'Cushion Cover 35"x35"' },
      { maxWidth: 35, maxLength: 47,  variant: '35"x47"',  label: 'Compact 35"x47"' },
      { maxWidth: 35, maxLength: 63,  variant: '35"x63"',  label: '2-Seat 35"x63"' },
      { maxWidth: 35, maxLength: 71,  variant: '35"x71"',  label: '2-3 Seat 35"x71"' },
      { maxWidth: 35, maxLength: 83,  variant: '35"x83"',  label: '3-Seat 35"x83"' },
      { maxWidth: 35, maxLength: 95,  variant: '35"x95"',  label: '4-Seat 35"x95"' },
      { maxWidth: 35, maxLength: 999, variant: '35"x95"',  label: '4-Seat 35"x95"', note: 'Your seat is longer than 95" — our largest single piece. Consider using 2 pieces, or send us a photo for help.' },
      { maxWidth: 43, maxLength: 43,  variant: '43"x43"',  label: 'Deep Cushion 43"x43"' },
      { maxWidth: 43, maxLength: 63,  variant: '43"x63"',  label: '2-Seat Deep 43"x63"' },
      { maxWidth: 43, maxLength: 71,  variant: '43"x71"',  label: '2-3 Seat Deep 43"x71"' },
      { maxWidth: 43, maxLength: 83,  variant: '43"x83"',  label: '3-Seat Deep 43"x83"' },
      { maxWidth: 43, maxLength: 95,  variant: '43"x95"',  label: '4-Seat Deep 43"x95"' },
      { maxWidth: 43, maxLength: 999, variant: '43"x95"',  label: '4-Seat Deep 43"x95"', note: 'Your seat is longer than 95" — our largest single piece. Consider using 2 pieces, or send us a photo for help.' },
    ],
  };

  const VARIANT_LOOKUP = {
    '28"x28"':  ['28\u2033x28\u2033 (1pc)', '28"x28" (1pc)'],
    '35"x35"':  ['35\u2033x35\u2033 (1pc)', '35"x35" (1pc)', 'Backrest 35"x35"', 'Backrest 35\u2033x35\u2033'],
    '35"x47"':  ['35\u2033x47\u2033 (1pc)', '35"x47" (1pc)'],
    '35"x63"':  ['35\u2033x63\u2033 (1pc)', '35"x63" (1pc)'],
    '35"x71"':  ['35\u2033x71\u2033 (1pc)', '35"x71" (1pc)'],
    '35"x83"':  ['35\u2033x83\u2033 (1pc)', '35"x83" (1pc)'],
    '35"x95"':  ['35\u2033x95\u2033 (1pc)', '35"x95" (1pc)'],
    '43"x43"':  ['43"x43" (1pc)', '43\u2033x43\u2033 (1pc)', 'Backrest 43"x43"', 'Backrest 43\u2033x43\u2033'],
    '43"x63"':  ['43"x63" (1pc)', '43\u2033x63\u2033 (1pc)'],
    '43"x71"':  ['43"x71" (1pc)', '43\u2033x71\u2033 (1pc)'],
    '43"x83"':  ['43"x83" (1pc)', '43\u2033x83\u2033 (1pc)'],
    '43"x95"':  ['43"x95" (1pc)', '43\u2033x95\u2033 (1pc)'],
  };

  function norm(s) {
    return s.replace(/[\u2033\u2032\u201C\u201D\u201E\u201F\u2018\u2019\u201A\u201B\u00AB\u00BB\u2039\u203A\u2033\u201C\u201D\u2018\u2019]/g, '"');
  }

  // ── SELECT VARIANT ON PAGE ──────────────────────────────────────────
  let variantCache = null;

  async function loadVariants() {
    if (variantCache) return variantCache;
    try {
      const handle = location.pathname.split('/products/')[1]?.split('?')[0];
      if (!handle) return null;
      const resp = await fetch(`/products/${handle}.json`);
      const data = await resp.json();
      variantCache = data.product.variants;
      return variantCache;
    } catch (e) { return null; }
  }
  loadVariants();

  function selectVariant(displaySize, part) {
    const candidates = VARIANT_LOOKUP[displaySize] || [displaySize];
    const sf = findMainFieldset();
    if (!sf) return false;

    // For backrest square sizes, prefer "Backrest ..." radio over generic "(1pc)" radio
    // because P0 has separate radios for backrest vs armrest with the same dimensions.
    const preferBackrest = part === 'backrest' && BACKREST_SQUARE.includes(displaySize);
    const sortedCandidates = preferBackrest
      ? [...candidates].sort((a, b) => {
          const aBack = a.toLowerCase().startsWith('backrest') ? 0 : 1;
          const bBack = b.toLowerCase().startsWith('backrest') ? 0 : 1;
          return aBack - bBack;
        })
      : candidates;

    const radios = sf.querySelectorAll('input[type="radio"]');
    for (const candidate of sortedCandidates) {
      for (const radio of radios) {
        if (norm(radio.value) === norm(candidate)) {
          radio.checked = true;
          applyVariant(radio.value);
          return true;
        }
      }
    }
    return false;
  }

  async function applyVariant(sizeValue) {
    const variants = await loadVariants();
    if (!variants) return;

    const colorFs = document.querySelector('fieldset[data-option-name="color"]');
    const currentColor = colorFs?.querySelector('input:checked')?.value || '';
    const sizeNorm = norm(sizeValue);

    const match = variants.find(v => {
      const parts = v.title.split(' / ');
      const vColor = parts[0] || '';
      const vSize = norm(parts.slice(1).join(' / '));
      return vSize === sizeNorm && (!currentColor || norm(vColor) === norm(currentColor));
    });
    if (!match) return;

    const price = parseFloat(match.price);
    const compareAt = match.compare_at_price ? parseFloat(match.compare_at_price) : null;
    const priceStr = '$' + price.toFixed(2).replace(/\.00$/, '');
    const compareStr = compareAt ? '$' + compareAt.toFixed(2).replace(/\.00$/, '') : '';
    const priceCents = Math.round(price * 100).toString();
    const compareCents = compareAt ? Math.round(compareAt * 100).toString() : '0';

    // Hidden form input
    document.querySelectorAll('form[action*="/cart/add"] input[name="id"]').forEach(el => {
      el.value = match.id;
    });

    // URL
    const url = new URL(location.href);
    url.searchParams.set('variant', match.id);
    history.replaceState({}, '', url.toString());

    // Update all .main-product-price containers (theme structure)
    document.querySelectorAll('.main-product-price').forEach(el => {
      const targetPrice = el.querySelector('.target-price');
      const targetCompare = el.querySelector('.target-compare-at-price');
      const saleSpan = el.querySelector('.price-sale');
      const compareEl = el.querySelector('s.price-compare');
      const pSpan = el.querySelector('p.price span');

      if (targetPrice) targetPrice.textContent = priceCents;
      if (targetCompare) targetCompare.textContent = compareCents;
      if (saleSpan) saleSpan.textContent = priceStr;
      if (pSpan) pSpan.textContent = priceStr;
      if (compareEl) {
        if (compareAt && compareAt > price) {
          compareEl.textContent = compareStr;
          compareEl.closest('small')?.style.removeProperty('display');
        } else {
          compareEl.textContent = '';
          compareEl.closest('small')?.style.setProperty('display', 'none');
        }
      }
    });
  }

  // ── FIND SIZE (calculator logic) ────────────────────────────────────
  function findSize(part, widthInch, lengthInch) {
    const rules = SIZE_RULES[part];
    if (!rules) return null;

    if (part === 'seat' || part === 'backrest') {
      const w = widthInch || 0;
      const l = lengthInch || 0;
      if (w > 43) {
        const partName = part === 'seat' ? 'seat depth' : 'backrest height';
        return { variant: '43"x95"', label: '43"x95" (closest match)', note: `Your ${partName} is ${Math.round(w)}" — wider than our max 43". The cover may not fully reach. Send us a photo and we'll help find the best option.` };
      }
      const isDeep = w > 35;
      const filtered = rules.filter(r => isDeep ? r.maxWidth > 35 : r.maxWidth <= 35);
      for (const rule of filtered) {
        if (l <= rule.maxLength) return rule;
      }
      return filtered[filtered.length - 1] || rules[rules.length - 1];
    } else {
      for (const rule of rules) {
        if (widthInch <= rule.maxWidth) return rule;
      }
      return rules[rules.length - 1];
    }
  }

  // ── SCROLL TO P0 + SWITCH TAB + PULSE ─────────────────────────────
  // P0 tabs are in order: armrest(1), backrest(2), seat(3), bundle(4)
  // Backrest square sizes (35x35, 43x43) live in P0 backrest tab.
  // Backrest strip sizes (35x63, 35x83...) live in P0 seat tab.
  const BACKREST_SQUARE = ['35"x35"', '43"x43"'];

  function getP0TabIndex(part, variant) {
    if (part === 'armrest') return 1;
    if (part === 'seat') return 3;
    if (part === 'backrest') {
      return BACKREST_SQUARE.includes(variant) ? 2 : 3;
    }
    return null;
  }

  function scrollToP0AndPulse(part, variant) {
    const sf = findMainFieldset();
    if (!sf) return;

    // Switch P0 to the correct tab so the selected label is visible
    const tabIndex = getP0TabIndex(part, variant);
    if (tabIndex) {
      const tabEl = sf.querySelector(`.cf-part-tabs .cf-part-tab:nth-child(${tabIndex})`);
      if (tabEl) tabEl.click();
    }

    // Switch P0 depth toggle if variant is deep (43" prefix)
    if (part === 'seat' && variant.startsWith('43"')) {
      const deepBtn = sf.querySelector('.cf-depth-toggle .cf-depth-btn[data-depth="deep"]');
      if (deepBtn && !deepBtn.classList.contains('active')) deepBtn.click();
    } else if (part === 'seat') {
      const stdBtn = sf.querySelector('.cf-depth-toggle .cf-depth-btn[data-depth="standard"]');
      if (stdBtn && !stdBtn.classList.contains('active')) stdBtn.click();
    }

    // Scroll P0 selector into view
    sf.scrollIntoView({ behavior: 'smooth', block: 'center' });

    // Find the checked label and pulse it
    setTimeout(() => {
      const checked = sf.querySelector('input:checked');
      if (!checked) return;
      const label = sf.querySelector(`label[for="${checked.id}"]`);
      if (!label) return;
      label.classList.add('cfh-pulse');
      setTimeout(() => label.classList.remove('cfh-pulse'), 1500);
    }, 400);
  }

  // ── STYLES ──────────────────────────────────────────────────────────
  const css = `
    /* ===== TRIGGER LINK ===== */
    #cfh-trigger.cfh-trigger {
      display: inline-flex !important;
      align-items: center !important;
      gap: 8px !important;
      padding: 10px 16px !important;
      margin: 10px 0 !important;
      font-size: 14px !important;
      font-weight: 600 !important;
      color: #8b6914 !important;
      cursor: pointer !important;
      background: #faf7f0 !important;
      border: 1.5px solid #e0dbd4 !important;
      border-radius: 8px !important;
      transition: all 0.15s !important;
      line-height: 1.4 !important;
      letter-spacing: 0 !important;
      text-transform: none !important;
    }
    #cfh-trigger.cfh-trigger:hover { border-color: #8b6914 !important; background: #f5f0e3 !important; }
    .cfh-trigger-icon { font-size: 17px !important; }
    .cfh-trigger-text { text-decoration: underline !important; text-underline-offset: 3px !important; text-decoration-color: #c9a84c !important; }

    /* ===== MODAL OVERLAY ===== */
    .cfh-overlay {
      position: fixed;
      inset: 0;
      z-index: 99999;
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 0;
      visibility: hidden;
      transition: opacity 0.25s, visibility 0.25s;
    }
    .cfh-overlay.open {
      opacity: 1;
      visibility: visible;
    }
    .cfh-backdrop {
      position: absolute;
      inset: 0;
      background: rgba(0, 0, 0, 0.45);
    }

    /* ===== MODAL BOX ===== */
    .cfh-modal {
      position: relative;
      width: 90%;
      max-width: 480px;
      max-height: 85vh;
      background: #fff;
      border-radius: 14px;
      overflow: hidden;
      display: flex;
      flex-direction: column;
      transform: translateY(20px) scale(0.97);
      transition: transform 0.25s cubic-bezier(0.22, 1, 0.36, 1);
      box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
    }
    .cfh-overlay.open .cfh-modal {
      transform: translateY(0) scale(1);
    }
    .cfh-modal *, .cfh-modal *::before, .cfh-modal *::after {
      box-sizing: border-box;
    }

    /* ===== MODAL HEADER ===== */
    .cfh-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 16px 20px;
      border-bottom: 1px solid #e0dbd4;
      background: #f9f5ec;
      flex-shrink: 0;
    }
    .cfh-header-title {
      font-size: 16px;
      font-weight: 700;
      color: #333;
    }
    .cfh-header-sub {
      font-size: 12px;
      color: #999;
      margin-top: 2px;
    }
    .cfh-close {
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      border: none;
      background: rgba(0,0,0,0.06);
      border-radius: 50%;
      font-size: 18px;
      color: #666;
      cursor: pointer;
      transition: background 0.15s;
      flex-shrink: 0;
    }
    .cfh-close:hover { background: rgba(0,0,0,0.12); }

    /* ===== MODAL BODY (scrollable) ===== */
    .cfh-body {
      flex: 1;
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
    }

    /* ===== MODE TABS ===== */
    .cfh-modes {
      display: grid;
      grid-template-columns: 1fr 1fr;
      border-bottom: 1.5px solid #e0dbd4;
    }
    .cfh-mode-tab {
      padding: 10px 16px;
      font-size: 13px;
      font-weight: 600;
      color: #888;
      text-align: center;
      cursor: pointer;
      background: #fafafa;
      border: none;
      border-bottom: 2px solid transparent;
      transition: all 0.15s;
    }
    .cfh-mode-tab:first-child { border-right: 1px solid #e0dbd4; }
    .cfh-mode-tab:hover { color: #555; background: #f5f3f0; }
    .cfh-mode-tab.active { color: #8b6914; background: #fff; border-bottom-color: #8b6914; }

    /* ===== SIZE CHART ===== */
    .cfh-sc { padding: 0; }

    /* Sub-tabs within size chart */
    .cfh-sc-tabs {
      display: flex;
      border-bottom: 1.5px solid #e0dbd4;
    }
    .cfh-sc-tab {
      flex: 1;
      padding: 9px 8px;
      font-size: 12px;
      font-weight: 600;
      color: #888;
      text-align: center;
      cursor: pointer;
      background: #fafafa;
      border: none;
      border-bottom: 2px solid transparent;
      transition: all 0.15s;
    }
    .cfh-sc-tab:not(:last-child) { border-right: 1px solid #e0dbd4; }
    .cfh-sc-tab:hover { color: #555; background: #f5f3f0; }
    .cfh-sc-tab.active { color: #8b6914; background: #fff; border-bottom-color: #8b6914; }

    .cfh-sc-panel { padding: 16px; display: none; }
    .cfh-sc-panel.active { display: block; }

    /* Armrest & Back: side-by-side compact tables */
    .cfh-sc-pair {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 12px;
    }
    @media (max-width: 420px) {
      .cfh-sc-pair { grid-template-columns: 1fr; }
    }
    .cfh-sc-mini { }
    .cfh-sc-mini-title {
      font-size: 13px;
      font-weight: 700;
      color: #333;
      margin-bottom: 6px;
      display: flex;
      align-items: center;
      gap: 6px;
    }
    .cfh-sc-mini-icon {
      color: #8b6914;
      line-height: 0;
      width: 36px;
      height: 26px;
      flex-shrink: 0;
    }
    .cfh-sc-mini-icon svg { width: 100%; height: 100%; }
    .cfh-sc-table {
      width: 100%;
      border-collapse: collapse;
      border: 1.5px solid #e0dbd4;
      border-radius: 8px;
      overflow: hidden;
      font-size: 12px;
    }
    .cfh-sc-table th {
      background: #8b6914;
      color: #fff;
      font-size: 10px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.3px;
      padding: 7px 10px;
      text-align: left;
    }
    .cfh-sc-table td {
      padding: 7px 10px;
      border-top: 1px solid #f0ede8;
      color: #555;
      vertical-align: middle;
    }
    .cfh-sc-table tr:nth-child(even) td { background: #faf8f4; }
    .cfh-sc-size { font-weight: 700; color: #333; }
    .cfh-sc-cm { font-size: 11px; color: #999; display: block; }

    /* Seat tables */
    .cfh-sc-seat-title {
      font-size: 13px;
      font-weight: 700;
      color: #333;
      margin-bottom: 6px;
      margin-top: 14px;
      display: flex;
      align-items: center;
      gap: 6px;
    }
    .cfh-sc-seat-title:first-child { margin-top: 0; }

    /* Full Sets: card grid */
    .cfh-sc-sets {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }
    .cfh-sc-set {
      border: 1.5px solid #e0dbd4;
      border-radius: 10px;
      overflow: visible;
      position: relative;
    }
    .cfh-sc-set-head {
      padding: 10px 14px;
      background: #f8f6f3;
      border-radius: 10px 10px 0 0;
      font-size: 14px;
      font-weight: 700;
      color: #333;
      display: flex;
      align-items: center;
      gap: 10px;
    }
    .cfh-sc-set-icon {
      color: #8b6914;
      line-height: 0;
      flex-shrink: 0;
      width: 56px;
      height: 32px;
      display: flex;
      align-items: center;
    }
    .cfh-sc-set-icon svg { width: 100%; height: 100%; }
    .cfh-sc-set-name { flex: 1; }
    .cfh-sc-set-pcs {
      font-size: 11px;
      font-weight: 600;
      color: #8b6914;
      background: #f9f5ec;
      padding: 2px 8px;
      border-radius: 4px;
      flex-shrink: 0;
    }
    .cfh-sc-set-pop {
      position: absolute;
      top: -9px;
      right: 14px;
      font-size: 9px;
      font-weight: 700;
      color: #fff;
      background: #8b6914;
      padding: 2px 8px;
      border-radius: 4px;
      z-index: 1;
    }
    .cfh-sc-set-row {
      display: grid;
      grid-template-columns: 1fr 1fr 50px;
      padding: 7px 14px;
      font-size: 13px;
      color: #555;
      border-top: 1px solid #f0ede8;
    }
    .cfh-sc-set-row.head {
      font-size: 10px;
      font-weight: 700;
      color: #999;
      text-transform: uppercase;
      letter-spacing: 0.3px;
      padding: 5px 14px;
    }
    .cfh-sc-set-part { font-weight: 500; color: #333; }
    .cfh-sc-set-size { font-weight: 700; color: #8b6914; }
    .cfh-sc-set-qty { text-align: right; color: #888; }

    .cfh-sc-note {
      font-size: 11px;
      color: #aaa;
      margin-top: 12px;
      line-height: 1.4;
      font-style: italic;
    }

    /* ===== SIZE FINDER ===== */
    .cfh-sf { padding: 16px; }

    .cfh-sf-step { margin-bottom: 16px; }
    .cfh-sf-step:last-child { margin-bottom: 0; }
    .cfh-sf-step-label {
      font-size: 12px;
      font-weight: 600;
      color: #8b6914;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin-bottom: 8px;
    }

    .cfh-sf-parts {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 8px;
    }
    .cfh-sf-part {
      padding: 14px 8px;
      border: 1.5px solid #e0dbd4;
      border-radius: 10px;
      text-align: center;
      cursor: pointer;
      background: #fff;
      transition: all 0.15s;
      font-size: 13px;
      font-weight: 500;
      color: #666;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
    }
    .cfh-sf-part:hover { border-color: #8b6914; background: #fffdf7; }
    .cfh-sf-part.active { border-color: #8b6914; background: #f9f5ec; color: #8b6914; box-shadow: 0 0 0 1px #8b6914; font-weight: 600; }
    .cfh-sf-part-icon { margin-bottom: 4px; display: flex; justify-content: center; line-height: 0; color: #bbb; height: 20px; }
    .cfh-sf-part-icon svg { width: 28px !important; height: 20px !important; max-width: 28px; max-height: 20px; }
    .cfh-sf-part.active .cfh-sf-part-icon { color: #8b6914; }

    .cfh-sf-inputs {
      display: none;
      gap: 12px;
      align-items: end;
    }
    .cfh-sf-inputs.visible { display: flex; }
    .cfh-sf-input-group { flex: 1; }
    .cfh-sf-input-label { font-size: 12px; color: #888; margin-bottom: 4px; display: block; }
    .cfh-sf-input {
      width: 100%;
      padding: 10px 12px;
      border: 1.5px solid #e0dbd4;
      border-radius: 8px;
      font-size: 15px;
      font-weight: 500;
      color: #333;
      outline: none;
      transition: border-color 0.15s;
    }
    .cfh-sf-input:focus { border-color: #8b6914; }
    .cfh-sf-input::placeholder { color: #ccc; }

    .cfh-sf-unit-toggle {
      display: inline-flex;
      border: 1px solid #e0dbd4;
      border-radius: 6px;
      overflow: hidden;
    }
    .cfh-sf-unit-btn {
      padding: 10px 14px;
      font-size: 13px;
      font-weight: 500;
      cursor: pointer;
      background: #fff;
      color: #888;
      border: none;
      transition: all 0.15s;
    }
    .cfh-sf-unit-btn:first-child { border-right: 1px solid #e0dbd4; }
    .cfh-sf-unit-btn.active { background: #8b6914; color: #fff; }

    .cfh-sf-guide {
      display: none;
      margin-top: 10px;
      padding: 12px;
      background: #f8f6f3;
      border-radius: 8px;
      font-size: 12px;
      color: #666;
      line-height: 1.5;
    }
    .cfh-sf-guide.visible { display: block; }
    .cfh-sf-guide strong { color: #333; }

    /* Result */
    .cfh-sf-result {
      display: none;
      padding: 14px 16px;
      background: #f0f9f0;
      border: 1.5px solid #4caf50;
      border-radius: 10px;
      margin-top: 12px;
    }
    .cfh-sf-result.visible { display: block; }
    .cfh-sf-result.warning { background: #fff8e1; border-color: #f9a825; }
    .cfh-sf-result-title { font-size: 13px; font-weight: 700; color: #2e7d32; margin-bottom: 4px; }
    .cfh-sf-result.warning .cfh-sf-result-title { color: #f57f17; }
    .cfh-sf-result-size { font-size: 16px; font-weight: 700; color: #333; margin-bottom: 6px; }
    .cfh-sf-result-note { font-size: 12px; color: #666; margin-bottom: 10px; line-height: 1.4; }
    .cfh-sf-result-btn {
      display: inline-block;
      padding: 10px 24px;
      background: #8b6914;
      color: #fff;
      border: none;
      border-radius: 8px;
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
      transition: background 0.15s;
      width: 100%;
      text-align: center;
    }
    .cfh-sf-result-btn:hover { background: #6d5210; }

    /* ===== FOOTER ===== */
    .cfh-footer {
      padding: 12px 20px;
      border-top: 1px solid #f0ede8;
      font-size: 12px;
      color: #888;
      line-height: 1.5;
      flex-shrink: 0;
    }
    .cfh-footer a { color: #8b6914; text-decoration: underline; }

    /* ===== PULSE ANIMATION (on P0 size card after select) ===== */
    @keyframes cfh-pulse-glow {
      0% { box-shadow: 0 0 0 0 rgba(139, 105, 20, 0.5); }
      50% { box-shadow: 0 0 0 8px rgba(139, 105, 20, 0); }
      100% { box-shadow: 0 0 0 0 rgba(139, 105, 20, 0); }
    }
    .cfh-pulse {
      animation: cfh-pulse-glow 0.5s ease-out 3 !important;
    }

    /* ===== MOBILE: BOTTOM SHEET ===== */
    @media (max-width: 599px) {
      .cfh-overlay {
        align-items: flex-end;
      }
      .cfh-modal {
        width: 100%;
        max-width: 100%;
        max-height: 90vh;
        border-radius: 16px 16px 0 0;
        transform: translateY(100%);
      }
      .cfh-overlay.open .cfh-modal {
        transform: translateY(0);
      }
      .cfh-sf-inputs { flex-direction: column; gap: 8px; }
      .cfh-sf-input-group { width: 100%; }
    }
  `;

  // ── PERSISTED STATE ──────────────────────────────────────────────────
  let activeMode = 'finder';
  let finderPart = null;
  let finderUnit = 'in';
  let finderWidth = '';
  let finderLength = '';
  let lastResult = null;

  // Modal DOM refs (created once, reused)
  let overlayEl = null;
  let modalBuilt = false;

  // ── OPEN / CLOSE MODAL ──────────────────────────────────────────────
  function openModal() {
    if (!overlayEl) buildModal();
    overlayEl.classList.add('open');
    document.body.style.overflow = 'hidden';
  }

  function closeModal() {
    if (!overlayEl) return;
    overlayEl.classList.remove('open');
    document.body.style.overflow = '';
  }

  // ── BUILD MODAL (once) ──────────────────────────────────────────────
  function buildModal() {
    if (modalBuilt) return;
    modalBuilt = true;

    // Inject styles
    if (!document.getElementById('cfh-styles')) {
      const style = document.createElement('style');
      style.id = 'cfh-styles';
      style.textContent = css;
      document.head.appendChild(style);
    }

    // Overlay
    overlayEl = document.createElement('div');
    overlayEl.className = 'cfh-overlay';

    // Backdrop
    const backdrop = document.createElement('div');
    backdrop.className = 'cfh-backdrop';
    backdrop.addEventListener('click', closeModal);

    // Modal box
    const modal = document.createElement('div');
    modal.className = 'cfh-modal';

    // Header
    const header = document.createElement('div');
    header.className = 'cfh-header';
    header.innerHTML = `
      <div>
        <div class="cfh-header-title">Find Your Perfect Size</div>
        <div class="cfh-header-sub">Measure once, order with confidence</div>
      </div>
    `;
    const closeBtn = document.createElement('button');
    closeBtn.className = 'cfh-close';
    closeBtn.innerHTML = '&times;';
    closeBtn.addEventListener('click', closeModal);
    header.appendChild(closeBtn);

    // Body (scrollable)
    const body = document.createElement('div');
    body.className = 'cfh-body';

    // Mode tabs
    const modes = document.createElement('div');
    modes.className = 'cfh-modes';
    modes.innerHTML = `
      <div class="cfh-mode-tab${activeMode === 'finder' ? ' active' : ''}" data-mode="finder">Size Finder</div>
      <div class="cfh-mode-tab${activeMode === 'chart' ? ' active' : ''}" data-mode="chart">Size Chart</div>
    `;

    // Size Chart content
    const scDiv = document.createElement('div');
    scDiv.className = 'cfh-sc';
    scDiv.style.display = activeMode === 'chart' ? '' : 'none';

    // Build Armrest & Back panel
    let armBackHtml = '<div class="cfh-sc-pair">';
    SC_ARM_BACK.forEach(tbl => {
      const iconSpan = tbl.icon ? `<span class="cfh-sc-mini-icon">${tbl.icon}</span>` : '';
      armBackHtml += `<div class="cfh-sc-mini"><div class="cfh-sc-mini-title">${iconSpan}${tbl.title}</div><table class="cfh-sc-table"><thead><tr><th>Width</th><th>Size</th></tr></thead><tbody>`;
      tbl.rows.forEach(r => {
        armBackHtml += `<tr><td>${r.measure}</td><td><span class="cfh-sc-size">${r.size}</span><span class="cfh-sc-cm">${r.cm}</span></td></tr>`;
      });
      armBackHtml += '</tbody></table></div>';
    });
    armBackHtml += '</div><div class="cfh-sc-note">Sold individually (1pc). Most sofas need 2 armrest pieces (left + right).<br>For backrest: buy 1 piece per seat cushion, or use a single long piece from the Seat/Chaise tab to cover the entire back at once.</div>';

    // Build Seat panel
    let seatHtml = '';
    SC_SEAT.forEach(tbl => {
      seatHtml += `<div class="cfh-sc-seat-title"><span class="cfh-sc-mini-icon">${PART_ICONS.seat}</span>${tbl.title}</div><table class="cfh-sc-table"><thead><tr><th>Length</th><th>Size</th></tr></thead><tbody>`;
      tbl.rows.forEach(r => {
        seatHtml += `<tr><td>${r.length}<span class="cfh-sc-cm">${r.lcm}</span></td><td><span class="cfh-sc-size">${r.size}</span><span class="cfh-sc-cm">${r.scm}</span></td></tr>`;
      });
      seatHtml += '</tbody></table>';
    });
    seatHtml += '<div class="cfh-sc-note">Cover sizes can be a few extra inches longer to allow for tucking into the sides.</div>';

    // Build Full Sets panel
    let setsHtml = '<div class="cfh-sc-sets">';
    SC_SETS.forEach(set => {
      const pop = set.popular ? `<span class="cfh-sc-set-pop">POPULAR</span>` : '';
      const iconHtml = set.icon ? `<span class="cfh-sc-set-icon">${set.icon}</span>` : '';
      setsHtml += `<div class="cfh-sc-set">${pop}<div class="cfh-sc-set-head">${iconHtml}<span class="cfh-sc-set-name">${set.name}</span><span class="cfh-sc-set-pcs">${set.pcs}</span></div>`;
      setsHtml += `<div class="cfh-sc-set-row head"><span>Part</span><span>Size</span><span style="text-align:right">Qty</span></div>`;
      set.items.forEach(item => {
        setsHtml += `<div class="cfh-sc-set-row"><span class="cfh-sc-set-part">${item.part}</span><span class="cfh-sc-set-size">${item.size}</span><span class="cfh-sc-set-qty">${item.qty}</span></div>`;
      });
      setsHtml += '</div>';
    });
    setsHtml += '</div><div class="cfh-sc-note">All sets use standard depth covers. For deep sofas (36"+), buy individual pieces in Seat & Chaise sizes above.</div>';

    // Assemble with sub-tabs
    scDiv.innerHTML = `
      <div class="cfh-sc-tabs">
        <div class="cfh-sc-tab active" data-sc="armback">Armrest & Back</div>
        <div class="cfh-sc-tab" data-sc="seat">Seat / Chaise</div>
        <div class="cfh-sc-tab" data-sc="sets">Full Sets</div>
      </div>
      <div class="cfh-sc-panel active" data-sc="armback">${armBackHtml}</div>
      <div class="cfh-sc-panel" data-sc="seat">${seatHtml}</div>
      <div class="cfh-sc-panel" data-sc="sets">${setsHtml}</div>
    `;

    // Sub-tab click handler
    scDiv.addEventListener('click', (e) => {
      const tab = e.target.closest('.cfh-sc-tab');
      if (!tab) return;
      const key = tab.dataset.sc;
      scDiv.querySelectorAll('.cfh-sc-tab').forEach(t => t.classList.toggle('active', t.dataset.sc === key));
      scDiv.querySelectorAll('.cfh-sc-panel').forEach(p => p.classList.toggle('active', p.dataset.sc === key));
    });

    // Size Finder content
    const sfDiv = document.createElement('div');
    sfDiv.className = 'cfh-sf';
    sfDiv.style.display = activeMode === 'finder' ? '' : 'none';
    sfDiv.innerHTML = `
      <div class="cfh-sf-step">
        <div class="cfh-sf-step-label">Step 1 — What are you covering?</div>
        <div class="cfh-sf-parts">
          <div class="cfh-sf-part" data-part="armrest">
            <span class="cfh-sf-part-icon"><svg viewBox="0 0 40 32" fill="none" width="16" height="12"><rect x="8" y="14" width="24" height="12" rx="2" stroke="#ccc" stroke-width="1.2" fill="none"/><rect x="8" y="6" width="24" height="10" rx="2" stroke="#ccc" stroke-width="1.2" fill="none"/><rect x="2" y="8" width="7" height="18" rx="2" fill="currentColor" opacity="0.85"/><rect x="31" y="8" width="7" height="18" rx="2" fill="currentColor" opacity="0.85"/></svg></span>
            Armrest
          </div>
          <div class="cfh-sf-part" data-part="backrest">
            <span class="cfh-sf-part-icon"><svg viewBox="0 0 40 32" fill="none" width="16" height="12"><rect x="8" y="14" width="24" height="12" rx="2" stroke="#ccc" stroke-width="1.2" fill="none"/><rect x="8" y="5" width="24" height="10" rx="2" fill="currentColor" opacity="0.85"/><rect x="2" y="8" width="7" height="18" rx="2" stroke="#ccc" stroke-width="1.2" fill="none"/><rect x="31" y="8" width="7" height="18" rx="2" stroke="#ccc" stroke-width="1.2" fill="none"/></svg></span>
            Backrest
          </div>
          <div class="cfh-sf-part" data-part="seat">
            <span class="cfh-sf-part-icon"><svg viewBox="0 0 40 32" fill="none" width="16" height="12"><rect x="8" y="14" width="24" height="12" rx="2" fill="currentColor" opacity="0.85"/><rect x="8" y="5" width="24" height="10" rx="2" stroke="#ccc" stroke-width="1.2" fill="none"/><rect x="2" y="8" width="7" height="18" rx="2" stroke="#ccc" stroke-width="1.2" fill="none"/><rect x="31" y="8" width="7" height="18" rx="2" stroke="#ccc" stroke-width="1.2" fill="none"/></svg></span>
            Seat / Chaise
          </div>
        </div>
      </div>
      <div class="cfh-sf-step">
        <div class="cfh-sf-guide" id="cfh-guide"></div>
      </div>
      <div class="cfh-sf-step">
        <div class="cfh-sf-step-label" id="cfh-measure-label" style="display:none">Step 2 — Enter your measurements</div>
        <div class="cfh-sf-inputs" id="cfh-inputs">
          <div class="cfh-sf-input-group">
            <span class="cfh-sf-input-label" id="cfh-width-label">Width</span>
            <input type="number" class="cfh-sf-input" id="cfh-width" placeholder="e.g. 30" min="1" max="200" inputmode="decimal">
          </div>
          <div class="cfh-sf-input-group" id="cfh-length-group" style="display:none">
            <span class="cfh-sf-input-label">Length</span>
            <input type="number" class="cfh-sf-input" id="cfh-length" placeholder="e.g. 72" min="1" max="200" inputmode="decimal">
          </div>
          <div class="cfh-sf-unit-toggle">
            <button class="cfh-sf-unit-btn active" data-unit="in">in</button>
            <button class="cfh-sf-unit-btn" data-unit="cm">cm</button>
          </div>
        </div>
      </div>
      <div class="cfh-sf-result" id="cfh-result">
        <div class="cfh-sf-result-title" id="cfh-result-title">Recommended size:</div>
        <div class="cfh-sf-result-size" id="cfh-result-size"></div>
        <div class="cfh-sf-result-note" id="cfh-result-note"></div>
        <button class="cfh-sf-result-btn" id="cfh-result-btn">Select This Size</button>
      </div>
    `;

    // Footer
    const footer = document.createElement('div');
    footer.className = 'cfh-footer';
    footer.innerHTML = `Still unsure? <a href="https://coverfect.com/pages/contact" target="_blank">Send us a photo of your sofa</a> — we'll pick the perfect size for you!`;

    // Assemble modal
    body.appendChild(modes);
    body.appendChild(scDiv);
    body.appendChild(sfDiv);
    modal.appendChild(header);
    modal.appendChild(body);
    modal.appendChild(footer);
    overlayEl.appendChild(backdrop);
    overlayEl.appendChild(modal);
    document.body.appendChild(overlayEl);

    // Close on Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && overlayEl.classList.contains('open')) closeModal();
    });

    // ── MODE TAB EVENTS ──
    modes.addEventListener('click', (e) => {
      const tab = e.target.closest('.cfh-mode-tab');
      if (!tab) return;
      activeMode = tab.dataset.mode;
      modes.querySelectorAll('.cfh-mode-tab').forEach(t => t.classList.remove('active'));
      tab.classList.add('active');
      scDiv.style.display = activeMode === 'chart' ? '' : 'none';
      sfDiv.style.display = activeMode === 'finder' ? '' : 'none';
    });

    // ── SIZE FINDER EVENTS ──
    const partsEl = sfDiv.querySelector('.cfh-sf-parts');
    const guideEl = sfDiv.querySelector('#cfh-guide');
    const inputsEl = sfDiv.querySelector('#cfh-inputs');
    const measureLabel = sfDiv.querySelector('#cfh-measure-label');
    const widthInput = sfDiv.querySelector('#cfh-width');
    const lengthInput = sfDiv.querySelector('#cfh-length');
    const lengthGroup = sfDiv.querySelector('#cfh-length-group');
    const widthLabel = sfDiv.querySelector('#cfh-width-label');
    const resultEl = sfDiv.querySelector('#cfh-result');
    const resultTitle = sfDiv.querySelector('#cfh-result-title');
    const resultSize = sfDiv.querySelector('#cfh-result-size');
    const resultNote = sfDiv.querySelector('#cfh-result-note');
    const resultBtn = sfDiv.querySelector('#cfh-result-btn');

    const GUIDE_TEXT = {
      armrest: `<strong>How to measure your armrest:</strong><br>Measure the <strong>width</strong> of one armrest from the outer edge to where the seat cushion begins. Most armrests are 20-35 inches wide.`,
      backrest: `<strong>How to measure your backrest:</strong><br>Measure <strong>width</strong> (height of the back cushion, top to seat) and <strong>length</strong> (total width of the back, armrest to armrest). You can use one long piece to cover the entire back.`,
      seat: `<strong>How to measure your seat:</strong><br>Measure <strong>width</strong> (front to back, the depth) and <strong>length</strong> (left to right armrest) of the seat area. Measure the flat sitting surface only.`,
    };

    // Restore persisted state
    if (finderPart) {
      const partBtn = partsEl.querySelector(`[data-part="${finderPart}"]`);
      if (partBtn) {
        partsEl.querySelectorAll('.cfh-sf-part').forEach(p => p.classList.remove('active'));
        partBtn.classList.add('active');
      }
      guideEl.innerHTML = GUIDE_TEXT[finderPart] || '';
      guideEl.classList.add('visible');
      measureLabel.style.display = '';
      inputsEl.classList.add('visible');
      const needsLength = finderPart === 'seat' || finderPart === 'backrest';
      lengthGroup.style.display = needsLength ? '' : 'none';
      widthLabel.textContent = needsLength ? 'Width (depth)' : 'Width';
      widthInput.value = finderWidth;
      lengthInput.value = finderLength;
      sfDiv.querySelectorAll('.cfh-sf-unit-btn').forEach(b => {
        b.classList.toggle('active', b.dataset.unit === finderUnit);
      });
      if (lastResult) {
        resultEl.classList.add('visible');
        if (lastResult.note) {
          resultEl.classList.add('warning');
          resultTitle.textContent = '⚠ Closest match:';
        } else {
          resultEl.classList.remove('warning');
          resultTitle.textContent = '✓ Recommended size:';
        }
        resultSize.textContent = lastResult.label;
        resultNote.textContent = lastResult.noteText || '';
        resultBtn.dataset.variant = lastResult.variant;
        resultBtn.dataset.part = finderPart;
      }
    }

    partsEl.addEventListener('click', (e) => {
      const partBtn = e.target.closest('.cfh-sf-part');
      if (!partBtn) return;
      finderPart = partBtn.dataset.part;
      partsEl.querySelectorAll('.cfh-sf-part').forEach(p => p.classList.remove('active'));
      partBtn.classList.add('active');

      guideEl.innerHTML = GUIDE_TEXT[finderPart] || '';
      guideEl.classList.add('visible');
      measureLabel.style.display = '';
      inputsEl.classList.add('visible');
      const needsLength = finderPart === 'seat' || finderPart === 'backrest';
      lengthGroup.style.display = needsLength ? '' : 'none';
      widthLabel.textContent = needsLength ? 'Width (depth)' : 'Width';

      widthInput.value = '';
      lengthInput.value = '';
      finderWidth = '';
      finderLength = '';
      lastResult = null;
      resultEl.classList.remove('visible');
      resultEl.classList.remove('warning');
      resultBtn.textContent = 'Select This Size';
      resultBtn.style.background = '';
      widthInput.focus();
    });

    // Unit toggle
    sfDiv.querySelectorAll('.cfh-sf-unit-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        finderUnit = btn.dataset.unit;
        sfDiv.querySelectorAll('.cfh-sf-unit-btn').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        calculate();
      });
    });

    function calculate() {
      if (!finderPart) return;
      let w = parseFloat(widthInput.value);
      let l = parseFloat(lengthInput.value);
      finderWidth = widthInput.value;
      finderLength = lengthInput.value;
      if (!w || w <= 0) { lastResult = null; resultEl.classList.remove('visible'); return; }
      if ((finderPart === 'seat' || finderPart === 'backrest') && (!l || l <= 0)) { lastResult = null; resultEl.classList.remove('visible'); return; }

      if (finderUnit === 'cm') {
        w = w / 2.54;
        if (l) l = l / 2.54;
      }

      const match = findSize(finderPart, w, l);
      if (!match) return;

      resultEl.classList.add('visible');
      if (match.note) {
        resultEl.classList.add('warning');
        resultTitle.textContent = '⚠ Closest match:';
      } else {
        resultEl.classList.remove('warning');
        resultTitle.textContent = '✓ Recommended size:';
      }
      resultSize.textContent = match.label;

      let noteText = '';
      if (finderPart === 'seat') {
        noteText = `Your seat measures ${Math.round(w)}" (depth) x ${Math.round(l)}" (length). The cover should be slightly larger for tucking.`;
      } else if (finderPart === 'backrest') {
        noteText = `Your backrest measures ${Math.round(w)}" (height) x ${Math.round(l)}" (total width). One piece covers the entire back.`;
      } else {
        noteText = `Your ${finderPart} measures ${Math.round(w)}" wide. Choose this size for best coverage.`;
      }
      if (match.note) noteText = match.note;
      resultNote.textContent = noteText;

      resultBtn.dataset.variant = match.variant;
      resultBtn.dataset.part = finderPart;
      lastResult = { variant: match.variant, label: match.label, note: match.note, noteText: noteText };

      resultBtn.textContent = 'Select This Size';
      resultBtn.style.background = '';
    }

    widthInput.addEventListener('input', calculate);
    lengthInput.addEventListener('input', calculate);

    // Select size button — close modal, scroll to P0, pulse
    resultBtn.addEventListener('click', () => {
      const variant = resultBtn.dataset.variant;
      if (!variant) return;

      const success = selectVariant(variant, finderPart);
      if (success) {
        closeModal();
        // Small delay for modal close animation, then scroll + pulse
        setTimeout(() => scrollToP0AndPulse(finderPart, variant), 300);
      } else {
        resultBtn.textContent = 'Size not found on this page';
        resultBtn.style.background = '#e53935';
      }
    });
  }

  // ── FIND MAIN FIELDSET (skip Quick View / hidden duplicates) ────────
  function findMainFieldset() {
    const all = document.querySelectorAll('fieldset[data-option-name="size"]');
    for (const fs of all) {
      if (fs.closest('.quick-view, [id*="quick-view"]')) continue;
      if (fs.getBoundingClientRect().width > 0) return fs;
    }
    return all[0] || null;
  }

  // ── ENSURE TRIGGER ──────────────────────────────────────────────────
  function ensureTrigger() {
    if (document.getElementById('cfh-trigger')) return;

    const sf = findMainFieldset();
    if (!sf) return;

    const trigger = document.createElement('div');
    trigger.id = 'cfh-trigger';
    trigger.setAttribute('role', 'button');
    trigger.setAttribute('tabindex', '0');
    // Apply styles inline to defeat Shopify theme overrides
    Object.assign(trigger.style, {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      padding: '10px 16px',
      margin: '10px 0',
      fontSize: '14px',
      fontWeight: '600',
      color: '#8b6914',
      cursor: 'pointer',
      background: '#faf7f0',
      border: '1.5px solid #e0dbd4',
      borderRadius: '8px',
      lineHeight: '1.4',
      letterSpacing: '0',
      textTransform: 'none',
      fontFamily: 'inherit',
      transition: 'all 0.15s',
    });
    trigger.innerHTML = `<span style="font-size:17px">📐</span><span style="text-decoration:underline;text-underline-offset:3px;text-decoration-color:#c9a84c">Not sure about your size? Find it here</span>`;
    trigger.addEventListener('click', openModal);
    trigger.addEventListener('keydown', (e) => { if (e.key === 'Enter') openModal(); });
    trigger.addEventListener('mouseenter', () => { trigger.style.borderColor = '#8b6914'; trigger.style.background = '#f5f0e3'; });
    trigger.addEventListener('mouseleave', () => { trigger.style.borderColor = '#e0dbd4'; trigger.style.background = '#faf7f0'; });

    sf.after(trigger);
  }

  // ── INTERCEPT "SIZE CHART" LINK ──────────────────────────────────────
  // Hijack the existing Shopify "Size chart" link/button to open our modal
  // on the Size Chart tab instead of the old static popup.
  function interceptSizeChartLink() {
    document.querySelectorAll('a, button').forEach(el => {
      const text = el.textContent.trim().toLowerCase();
      if (text === 'size chart' || text === '📐 size chart') {
        el.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          activeMode = 'chart';
          openModal();
          // Switch to chart tab visually
          const overlay = document.querySelector('.cfh-overlay');
          if (overlay) {
            overlay.querySelectorAll('.cfh-mode-tab').forEach(t => {
              t.classList.toggle('active', t.dataset.mode === 'chart');
            });
            const sc = overlay.querySelector('.cfh-sc');
            const sf = overlay.querySelector('.cfh-sf');
            if (sc) sc.style.display = '';
            if (sf) sf.style.display = 'none';
          }
        }, true);
      }
    });
  }

  // ── INIT ────────────────────────────────────────────────────────────
  function init() {
    ensureTrigger();
    interceptSizeChartLink();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Watch for Shopify re-renders (color change etc.)
  new MutationObserver(() => {
    if (!document.getElementById('cfh-trigger') && findMainFieldset()) {
      ensureTrigger();
    }
  }).observe(document.body, { childList: true, subtree: true });

})();
